/**
 * Systèmes de notation des désordres.
 *
 * Chaque catalogue porte le sien (colonne `type`). Les libellés changent d'un
 * système à l'autre — "2E" en IQOA, "D2E" en PM2I — mais recouvrent la même
 * échelle de gravité. C'est ce niveau canonique qui pilote couleurs, tris et
 * règles métier, jamais le libellé : ajouter un système se limite alors à
 * ajouter une entrée ici.
 *
 * Les libellés ne se recoupent pas entre systèmes, ce qui permet de retrouver la
 * gravité d'une note à partir d'elle seule, sans connaître son catalogue.
 */

/** Gravité commune à tous les systèmes, du plus bénin au plus urgent. */
export type SeverityLevel =
  | "none"
  | "minor"
  | "moderate"
  | "moderateEvolutive"
  | "severe"
  | "severeUrgent";

export interface NotationGrade {
  /** Libellé stocké en base dans DisorderGrade.class / Finding.notation. */
  value: string;
  level: SeverityLevel;
}

export interface NotationSystem {
  /** Valeur de Catalogue.type qui déclenche ce système. */
  code: string;
  label: string;
  grades: NotationGrade[];
}

export const NOTATION_SYSTEMS: NotationSystem[] = [
  {
    code: "IQOA",
    label: "IQOA",
    grades: [
      { value: "0", level: "none" },
      { value: "1", level: "minor" },
      { value: "2", level: "moderate" },
      { value: "2E", level: "moderateEvolutive" },
      { value: "3", level: "severe" },
      { value: "3U", level: "severeUrgent" },
    ],
  },
  {
    code: "PM2I",
    label: "PM2I",
    grades: [
      { value: "D1", level: "minor" },
      { value: "D2", level: "moderate" },
      { value: "D2E", level: "moderateEvolutive" },
      { value: "D3", level: "severe" },
      { value: "D3P", level: "severeUrgent" },
    ],
  },
];

const DEFAULT_SYSTEM = NOTATION_SYSTEMS[0];

/**
 * Notation libre : l'inspecteur saisit ce qu'il veut. Ce n'est pas un système au
 * même titre que les autres — il n'a pas de grades — mais il se choisit au même
 * endroit, d'où sa présence dans la liste proposée à l'utilisateur.
 */
export const CUSTOM_SYSTEM_CODE = "CUSTOM";

export type NotationSystemCode = string;

/** Codes proposés dans le sélecteur, notation libre comprise. */
export const SELECTABLE_SYSTEM_CODES: NotationSystemCode[] = [
  ...NOTATION_SYSTEMS.map((s) => s.code),
  CUSTOM_SYSTEM_CODE,
];

export const isCustomSystem = (code?: string | null): boolean =>
  code === CUSTOM_SYSTEM_CODE;

export const getSystemByCode = (
  code?: string | null
): NotationSystem | undefined =>
  NOTATION_SYSTEMS.find((system) => system.code === code);

/**
 * Traduit une note d'un système vers un autre, en passant par le niveau de
 * gravité commun : "2E" (IQOA) devient "D2E" (PM2I) et réciproquement.
 *
 * - vers la notation libre, la note est reprise telle quelle comme texte ;
 * - depuis la notation libre, une saisie qui ne correspond à aucun niveau connu
 *   n'a pas d'équivalent : la note est vidée plutôt que d'inventer une valeur.
 */
export const convertGrade = (
  value: string | null | undefined,
  toSystemCode: NotationSystemCode
): string => {
  if (!value) return "";
  if (isCustomSystem(toSystemCode)) return value;

  const target = getSystemByCode(toSystemCode);
  if (!target) return "";

  const level = getSeverityLevel(value);
  if (!level) return "";

  return target.grades.find((g) => g.level === level)?.value ?? "";
};

/**
 * Système d'un catalogue. Les catalogues historiques ont un `type` saisi
 * librement ("iqoa", "IQOA", parfois vide) : comparaison insensible à la casse,
 * et repli sur IQOA qui était le seul système jusqu'ici.
 */
export const getNotationSystem = (
  catalogueType?: string | null
): NotationSystem => {
  const normalized = (catalogueType ?? "").trim().toUpperCase();
  return (
    NOTATION_SYSTEMS.find((system) => system.code === normalized) ??
    DEFAULT_SYSTEM
  );
};

/** Système auquel appartient une note, déduit du libellé seul. */
export const findSystemForGrade = (
  gradeValue?: string | null
): NotationSystem | undefined =>
  gradeValue
    ? NOTATION_SYSTEMS.find((system) =>
        system.grades.some((g) => g.value === gradeValue)
      )
    : undefined;

export const getSeverityLevel = (
  gradeValue?: string | null
): SeverityLevel | undefined =>
  findSystemForGrade(gradeValue)?.grades.find((g) => g.value === gradeValue)
    ?.level;

/**
 * Rang d'une note dans son système, pour trier des grades hétérogènes.
 * Les notes inconnues sont renvoyées en fin de liste.
 */
export const getGradeOrder = (gradeValue?: string | null): number => {
  const system = findSystemForGrade(gradeValue);
  if (!system) return Number.MAX_SAFE_INTEGER;
  return system.grades.findIndex((g) => g.value === gradeValue);
};

/** Niveaux considérés comme graves (préconisations, alertes de rapport). */
export const SEVERE_LEVELS: SeverityLevel[] = [
  "moderateEvolutive",
  "severe",
  "severeUrgent",
];

export const isSevereGrade = (gradeValue?: string | null): boolean => {
  const level = getSeverityLevel(gradeValue);
  return !!level && SEVERE_LEVELS.includes(level);
};
